<template>
  <div class="payment-methods">
    <div class="methods-label">{{ $t('pay.payment.methodsLabel') }}</div>
    <div class="methods-list">
      <div
        v-for="method in methods"
        :key="method.method"
        class="method-item"
        :class="{ 'is-selected': selected.method === method.method }"
        @click="handleMethodClick(method)"
      >
        <div class="method-icon">
          <img v-if="method.icon" :src="method.icon" :alt="method.label" />
          <Icon v-else name="material-symbols:payment" />
        </div>
        <span class="method-label">{{ getLabel(method) }}</span>
        <div class="method-radio">
          <div class="radio-dot" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PaymentMethod } from '~/stores/pay'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

interface Props {
  methods: PaymentMethod[]
  selected: PaymentMethod
}

defineProps<Props>()

const emit = defineEmits<{
  change: [method: PaymentMethod]
}>()

const handleMethodClick = (method: PaymentMethod) => {
  emit('change', method)
}

const getLabel = (method: PaymentMethod) => {
  const map: Record<string, string> = {
    wechat: t('pay.payment.methods.wechat')
  }
  return map[method.method] || method.label
}
</script>

<style scoped lang="scss">
.payment-methods {
  margin-bottom: 24px;
}

.methods-label {
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 12px;
}

.methods-list {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.method-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4A90E2;
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
  }

  &.is-selected {
    border-color: #4A90E2;
    background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
    box-shadow: 0 2px 12px rgba(74, 144, 226, 0.15);

    .radio-dot {
      background: #4A90E2;
    }
  }
}

.method-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  svg {
    font-size: 32px;
    color: #6C757D;
  }
}

.method-label {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  color: #212529;
}

.method-radio {
  width: 20px;
  height: 20px;
  border: 2px solid #ADB5BD;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  .is-selected & {
    border-color: #4A90E2;
  }

  .radio-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: transparent;
    transition: all 0.3s ease;
  }
}

@media (max-width: 768px) {
  .methods-list {
    flex-direction: column;
  }

  .method-item {
    min-width: auto;
  }
}
</style>
